import { useMemo } from 'react';
import dynamic from 'next/dynamic';
import Separator from '@discover-ui/separator';
import { cn } from '@discover-lib/utils';
import {
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
} from '@discover-data/stores/restaurant/model/times/default';
import { type RestaurantTagType } from '@discover-data/stores/restaurant/gql/fragments/restaurantTag';
import { type SuburbType } from '@discover-data/gql/fragments/suburb';
import { type PeopleOption } from '@discover-data/stores/hooks/people/options';
import { useStoreState } from '@discover-data/stores/restaurant/hooks';
import useEndTime from '@discover-data/stores/hooks/times/end/useEndTime';
import useShouldShowFavouriteFilter from '@discover-data/stores/hooks/favourite/useShouldShow';
import useStartTime from '@discover-data/stores/hooks/times/start/useStartTime';
import ExitRegularOnlyButton from '@discover-components/restaurant/list/exitRegularTabeOnlyButton';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import SessionSelect from '../sessionSelect';
import PeopleSelect from '../peopleSelect';
import DateSelect from '../dateSelect';
import SuburbSelect from '../suburb/select';
import SortSelect from '../sortSelect';
import DiscountOnlyToggle from '../discountOnly/toggle';
import FavouriteOnlyToggle from '../favouriteOnly/toggle';
import TagFilterComboBox from '../tags/comboBox';
import overloadTags from '../tags/overloadTags';
import TimeRangeDialog from '../time/dialog';

const ignoredTagCategories = ['Entry requirements'];

const RestaurantFiltersDrawer = dynamic(() => import('../drawer'));

function button(className?: string) {
  return cn('min-w-fit justify-center font-medium', className);
}

export type MobileTopBarProps = {
  tags: RestaurantTagType[];
  suburbs: SuburbType[];
  peopleOptions?: PeopleOption[] | null;
  minDiners?: PeopleOption | null;
  maxDiners?: PeopleOption | null;
};

export default function MobileTopBar({
  tags,
  suburbs,
  peopleOptions,
  minDiners,
  maxDiners,
}: MobileTopBarProps) {
  const isSuburbDefault = useStoreState((state) => state.suburb.isDefault);
  const discoverEnabled = useStoreState((state) => state.discoverEnabled);
  const regularOnly = useStoreState((state) => state.regularOnly.value);
  const [startTime] = useStartTime();
  const [endTime] = useEndTime();
  const hasTime = startTime !== DEFAULT_START_TIME || endTime !== DEFAULT_END_TIME;
  const tagIds = useStoreState((state) => state.tagIds.value);
  const sort = useStoreState((state) => state.sort.value);
  const sortDefault = sort === 'FEATURED_NEW_TOP';
  const discountOnly = useStoreState((state) => state.discountOnly.value);
  const favouriteOnly = useStoreState((state) => state.favourite.value);
  const shouldShowFavourite = useShouldShowFavouriteFilter();

  const relevantTags = useMemo(
    () => tags.filter((tag) => tag.category && !ignoredTagCategories.includes(tag.category)),
    [tags]
  );

  const tagsByCategory = useMemo(
    () =>
      relevantTags.reduce((acc: Record<string, typeof tags>, tag) => {
        if (tag.category) {
          acc[tag.category] = [...(acc[tag.category] || []), tag];
        }
        return acc;
      }, {}),
    [relevantTags]
  );

  const filterComponents = useMemo(() => {
    const filters = [];

    // Always add drawer first
    filters.push({
      id: 'drawer',
      component: (
        <RestaurantFiltersDrawer
          triggerProps={{ kind: 'outline', scale: 'sm', round: true }}
          tags={relevantTags}
          triggerClassName={button('ml-2')}
          suburbs={suburbs}
        />
      ),
      isActive: false,
    });

    if (discoverEnabled && !regularOnly) {
      filters.push({
        id: 'discount',
        component: <DiscountOnlyToggle scale="sm" kind="outline" round className={button()} />,
        isActive: discountOnly,
      });
    }

    if (shouldShowFavourite) {
      filters.push({
        id: 'favourite',
        component: <FavouriteOnlyToggle scale="sm" kind="outline" round className={button()} />,
        isActive: favouriteOnly,
      });
    }

    if (regularOnly) {
      filters.push({
        id: 'regular',
        component: (
          <ExitRegularOnlyButton
            scale="sm"
            className={button('rounded-full')}
            content="Regular tables only"
            kind="normal"
          />
        ),
        isActive: true,
      });
    }

    filters.push({
      id: 'sort',
      component: (
        <SortSelect
          triggerProps={{
            kind: !sortDefault ? 'normal' : 'outline',
            scale: 'sm',
            round: true,
            className: button(),
          }}
        />
      ),
      isActive: !sortDefault,
    });

    filters.push({
      id: 'suburb',
      component: (
        <SuburbSelect
          triggerProps={{
            kind: isSuburbDefault ? 'outline' : 'normal',
            scale: 'sm',
            round: true,
            className: button(),
          }}
          suburbs={suburbs}
        />
      ),
      isActive: !isSuburbDefault,
    });

    filters.push({
      id: 'time',
      component: (
        <TimeRangeDialog
          triggerProps={{
            kind: hasTime ? 'normal' : 'outline',
            scale: 'sm',
            round: true,
            className: button(),
          }}
        />
      ),
      isActive: hasTime,
    });

    Object.entries(tagsByCategory).forEach(([category, categoryTags]) => {
      const hasActiveTag = categoryTags.some((tag) => tag.id && tagIds.includes(tag.id));
      filters.push({
        id: `tag-${category}`,
        component: (
          <TagFilterComboBox
            key={category}
            category={category}
            tags={overloadTags(category, categoryTags)}
            triggerProps={{
              kind: hasActiveTag ? 'normal' : 'outline',
              scale: 'sm',
              round: true,
              className: button(),
            }}
          />
        ),
        isActive: hasActiveTag,
      });
    });

    return filters;
  }, [
    relevantTags,
    suburbs,
    discoverEnabled,
    regularOnly,
    sortDefault,
    isSuburbDefault,
    hasTime,
    tagsByCategory,
    discountOnly,
    favouriteOnly,
    tagIds,
    shouldShowFavourite,
  ]);

  const sortedFilters = useMemo(() => {
    const [drawer, ...rest] = filterComponents;

    const activeFilters = rest.filter((filter) => filter.isActive);
    const inactiveFilters = rest.filter((filter) => !filter.isActive);

    return [drawer, ...activeFilters, ...inactiveFilters];
  }, [filterComponents]);

  return (
    <TrackProvider section="restaurant filters">
      <div
        data-restaurant-filter-bar
        className="sticky top-safe-top z-30 flex flex-col items-stretch gap-2 bg-white py-2 pb-0 lg:hidden">
        <div className="mx-2 flex flex-row items-center justify-between gap-1 rounded-full border border-input p-1">
          <SessionSelect
            triggerProps={{
              kind: 'outline',
              scale: 'sm',
              className: button('min-w-0 flex-1 px-0 rounded-l-full rounded-r-none outline-0'),
            }}
          />
          <Separator className="my-2" orientation="vertical" />
          <DateSelect
            triggerProps={{
              kind: 'outline',
              scale: 'sm',
              className: button('min-w-0 flex-1 px-0 rounded-none outline-0'),
            }}
          />
          <Separator className="my-2" orientation="vertical" />
          <PeopleSelect
            peopleOptions={peopleOptions}
            minDiners={minDiners}
            maxDiners={maxDiners}
            triggerProps={{
              kind: 'outline',
              scale: 'sm',
              className: button('min-w-0 flex-1 px-0 rounded-r-full rounded-l-none outline-0'),
            }}
          />
        </div>
        <div className="no-scrollbar flex flex-row items-center gap-1 overflow-x-auto pb-2 pt-1">
          {sortedFilters.map((filter) => (
            <div key={filter.id} className="shrink-0 px-0.5">
              {filter.component}
            </div>
          ))}
        </div>
      </div>
    </TrackProvider>
  );
}
